'use client';

import { useState, useMemo } from 'react';
import { AllConvertersCard } from './AllConvertersCard';
import { CategoryList } from './CategoryList';
import { ConverterCard } from './ConverterCard';
import { PopularConversionsCard } from './PopularConversionsCard';
import { converters } from '@/lib/converters';
import { categories } from '@/lib/data/categories';
import { Search, Zap, Shield, MousePointer2, HelpCircle } from 'lucide-react';
import Link from 'next/link';

interface HomePageProps {
  popularConversions?: {
    from: string;
    to: string;
    count: number;
  }[];
}

export function HomePage({ popularConversions }: HomePageProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();

    // Search both converter names and unit names/symbols
    return Object.values(converters)
      .filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.units.some(u => u.name.toLowerCase().includes(query) || u.symbol.toLowerCase().includes(query))
      )
      .slice(0, 8);
  }, [searchQuery]);

  const faqs = [
    {
      question: "How accurate is the Unit Converter?",
      answer: "Our tool uses high-precision mathematical constants and standard international conversion factors (SI) to ensure results are accurate for scientific, professional, and everyday use."
    },
    {
      question: "Which categories are supported?",
      answer: "We support over 80 categories including Common (Length, Weight, Temp), Engineering, Electricity, Fluids, Heat, Light, Magnetism, and Radiology."
    },
    {
      question: "Is this unit converter free to use?",
      answer: "Yes, AllUnitConverter is 100% free. We provide unlimited conversions across all categories including advanced engineering and scientific units without any registration."
    },
    {
      question: "Can I use this on my mobile phone?",
      answer: "Yes! AllUnitConverter is fully responsive and optimized for mobile browsers, allowing you to convert units on the go without installing any app."
    }
  ];

  return (
    <div className="space-y-12 pb-20">
      {/* Hero Section with Search */}
      <section className="relative bg-emerald-600 rounded-3xl p-8 md:p-16 text-white shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-400/20 rounded-full -ml-32 -mb-32 blur-3xl pointer-events-none" />

        <div className="relative z-30 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
            Fast, Accurate & <br />
            <span className="text-emerald-200">Free Unit Conversions</span>
          </h1>
          <p className="text-lg text-emerald-50 mb-8 opacity-90 max-w-xl font-medium">
            Instantly convert between thousands of units across 80+ categories. Precise, fast, and easy to use.
          </p>

          <div className="relative max-w-2xl group">
            <input
              type="text"
              placeholder="Search for any unit (e.g., 'meter' or 'newton')..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-14 pr-6 py-5 rounded-2xl bg-white text-gray-900 shadow-xl focus:ring-4 focus:ring-emerald-400/30 outline-none transition-all text-lg font-medium"
            />
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-emerald-600" />

            {/* Search Results Dropdown - Relative to container, z-index high */}
            {searchResults.length > 0 && (
              <div className="absolute z-[100] w-full mt-3 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden text-gray-900 animate-in fade-in slide-in-from-top-2">
                <div className="p-2 max-h-[400px] overflow-y-auto">
                  {searchResults.map(c => {
                    const category = categories.find(cat => cat.converters.includes(c.id));
                    const categorySlug = category?.name.toLowerCase().replace(/\s+/g, '-');
                    const converterSlug = c.name.toLowerCase().replace(/\s+/g, '-');

                    return (
                      <Link
                        key={c.id}
                        href={categorySlug ? `/${categorySlug}/${converterSlug}` : '#'}
                        className="flex items-center gap-3 p-3 hover:bg-emerald-50 rounded-xl transition-colors group"
                      >
                        <div className="bg-emerald-100 p-2 rounded-lg group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                          <Zap className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">{c.name}</p>
                          <p className="text-xs text-gray-400 uppercase tracking-wider">{category?.name}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border shadow-sm flex items-start gap-4">
          <div className="bg-emerald-100 p-3 rounded-xl">
            <Zap className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 mb-1 text-sm uppercase tracking-wider">Instant Results</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Get conversion results in real-time as you type, no reload required.</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border shadow-sm flex items-start gap-4">
          <div className="bg-emerald-100 p-3 rounded-xl">
            <Shield className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 mb-1 text-sm uppercase tracking-wider">Secure & Private</h3>
            <p className="text-gray-500 text-sm leading-relaxed">No data is sent to our servers. All calculations happen in your browser.</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border shadow-sm flex items-start gap-4">
          <div className="bg-emerald-100 p-3 rounded-xl">
            <MousePointer2 className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 mb-1 text-sm uppercase tracking-wider">One-Click Tools</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Easily swap units and share results with a single click.</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <ConverterCard converter={Object.values(converters)[35]} />
          <PopularConversionsCard popularConversions={popularConversions} />
          <CategoryList />

          {/* Main Content Area FAQ */}
          <section className="bg-white border rounded-2xl p-8 shadow-sm">
            <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-gray-900">
              <HelpCircle className="w-6 h-6 text-emerald-600" />
              Frequently Asked Questions
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              {faqs.map((f, i) => (
                <div key={i} className="space-y-3">
                  <h4 className="font-bold text-gray-900 text-base">{f.question}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="space-y-8">
          <div className="sticky top-4">
            <AllConvertersCard />
          </div>
        </div>
      </div>
    </div>
  );
}
